import { GenrePill } from "@/components/builder/stories/genre-pill";
import { extractGenres } from "@/components/builder/stories/list/helper";
import PlusMinusTransition from "@/components/icons/plus-minus-transition";
import { useTranslations } from "@/libs/base/components/locale-provider";
import { cn } from "@/libs/base/utils/layout";
import { Transition } from "@headlessui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useStoriesListContext } from "./context";

const TOP_PADDING = 32;
const BOTTOM_PADDING = 32;
const ELEMENT_HEIGHT = 32;

function calculateMaxHeight(subElementsLength: number) {
  return TOP_PADDING + subElementsLength * ELEMENT_HEIGHT + BOTTOM_PADDING;
}

export default function StoriesGenreFilter() {
  const ctx = useStoriesListContext();
  const {
    builder: { stories: translations },
  } = useTranslations();
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen((open) => {
      if (open) return open;
      return ctx.filter.genres.length > 0;
    });
  }, [ctx.filter.genres.length]);

  const selectGenre = useCallback(
    (genre: string) => {
      let genres: string[];
      if (ctx.filter.genres.includes(genre)) {
        genres = ctx.filter.genres.filter((selectedGenre) => selectedGenre !== genre);
      } else {
        genres = [...ctx.filter.genres, genre];
      }

      ctx.selectGenres(genres);
    },
    [ctx.filter.genres],
  );

  const genres = extractGenres(ctx.genres);

  const closeAnimationState = "transform scale-y-0 max-h-0";
  const openAnimationState = "transform scale-y-100";

  return (
    <div>
      <button
        type="button"
        className={cn(
          "mb-2 flex w-full items-center justify-between gap-2 rounded-full px-5 py-2 transition duration-500",
          open ? "bg-lightgray-100 text-lightgray-300" : "bg-black text-white",
        )}
        onClick={(e) => {
          e.preventDefault();
          setOpen(!open);
        }}
        aria-expanded={open}
        aria-controls="stories-genre-filter"
      >
        <span>{translations.genres}</span>

        <PlusMinusTransition
          className={cn("pointer-events-none duration-500", open ? "text-black" : "text-white")}
          showMode={open ? "minus" : "plus"}
        />
      </button>

      <Transition
        ref={ref}
        beforeEnter={() => {
          ref.current && ref.current.style.setProperty(`max-height`, `${calculateMaxHeight(genres.length)}px`);
        }}
        beforeLeave={() => {
          ref.current && ref.current.style.setProperty(`max-height`, `0px`);
        }}
        enter="transition-all duration-500 origin-top"
        enterFrom={closeAnimationState}
        enterTo={openAnimationState}
        leave="transition-all duration-500 origin-top"
        leaveFrom={openAnimationState}
        leaveTo={closeAnimationState}
        show={open}
      >
        <div id="stories-genre-filter" className="flex flex-row flex-wrap items-start gap-3.5 sm:gap-2.5">
          {genres.map((genre) => {
            const active = ctx.filter.genres.includes(genre);

            return <GenrePill active={active} key={genre} genre={genre} onClick={() => selectGenre(genre)} />;
          })}
        </div>
      </Transition>
    </div>
  );
}
